import { InjectionToken } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { saveAs } from 'file-saver';

export type Saver = (blob: Blob, filename?: string) => void;

export const SAVER = new InjectionToken<Saver>('saver');

/** Raw base64 (no `data:…;base64,` preamble) — what Capacitor Filesystem wants. */
export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = (reader.result as string) || '';
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Save a blob to the user's device.
 *
 * Web: file-saver's `<a download>`, unchanged.
 *
 * Capacitor native: `<a download>` is a NO-OP inside the Android WebView, so
 * mobile downloads were simply dead (G7). There we write the bytes into the
 * app's cache directory and hand the resulting file to the OS Share sheet —
 * "save to Drive / open in… / share" — which is the platform-idiomatic
 * equivalent. Plugins are dynamically imported and the branch is guarded by
 * `Capacitor.isNativePlatform()`, so no native code enters the web bundle
 * (NativeShellService pattern).
 */
export async function saveBlob(blob: Blob, filename = 'attachment'): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        saveAs(blob, filename);
        return;
    }

    let uri: string;
    try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const written = await Filesystem.writeFile({
            path: filename,
            data: await blobToBase64(blob),
            directory: Directory.Cache,
            recursive: true,
        });
        uri = written.uri;
    } catch (err: unknown) {
        // Better a dead-end than no attempt: fall back to the web path.
        console.warn('[saver] native write failed, falling back to a web download', err);
        saveAs(blob, filename);
        return;
    }

    try {
        const { Share } = await import('@capacitor/share');
        await Share.share({ title: filename, url: uri, dialogTitle: `Save ${filename}` });
    } catch (err: unknown) {
        // Dismissing the Share sheet rejects. The file is already written, so this
        // is not a failure the user needs to hear about.
        console.debug('[saver] share sheet dismissed', err);
    }
}

/**
 * The injected `Saver`. Kept fire-and-forget (void) so every existing
 * `this.save(blob, name)` call site is unchanged.
 */
export function getSaver(): Saver {
    return (blob: Blob, filename?: string) => {
        saveBlob(blob, filename).catch((err: unknown) => console.error('[saver] save failed', err));
    };
}
